import { FunctionComponent } from 'react';
import { Country, Locale } from '../../../utils/config';
import AdyenBancontactCardPayment from '../../payment/AdyenBancontactCardPayment';
import AdyenBlikPayment from '../../payment/AdyenBlikPayment';
import AdyenCreditcardPayment from '../../payment/AdyenCreditcardPayment';
import AdyenIdealPayment from '../../payment/AdyenIdealPayment';
import AdyenOnlineBankingPlPayment from '../../payment/AdyenOnlineBankingPlPayment';
import AfterpayPayment from '../../payment/AfterpayPayment';
import AmazonPayment from '../../payment/AmazonPayment';
import InstallmentsForm from '../../payment/InstallmentsForm';
import KlarnaPayment from '../../payment/KlarnaPayment';
import PaypalPayment from '../../payment/PaypalPayment';
import RatepayPayment from '../../payment/RatepayPayment';
import SpayAdyenIdealPayment from '../../payment/SpayAdyenIdealPayment';
import UnzerCreditcard from '../../payment/Unzer/UnzerCreditcard';
import UnzerIdeal from '../../payment/Unzer/UnzerIdeal';
import UnzerInstallment from '../../payment/Unzer/UnzerInstallment';
import CreditCardForm from './CreditCardForm';
import './style.scss';

type AdditionalPaymentMethodConfig = {
  i18nKey?: string;
  useI18nKeyAsIconKey?: boolean;
  iconKeys?: string[];
  countryIconKeys?: Partial<Record<Country, string[]>>;
  localeIconKeys?: Partial<Record<Locale, string[]>>;
  form?: FunctionComponent<{ isSPAY?: boolean; isRecurring?: boolean }>;
  modal?: 'always' | 'desktopOnly';
};

const additionalPaymentConfig: Record<string, AdditionalPaymentMethodConfig> = {
  standard_cheque: { i18nKey: 'cheque' },
  standard_creditcard: { i18nKey: 'creditCard', form: CreditCardForm, iconKeys: ['mastercard', 'visa'] },
  standard_invoice: { i18nKey: 'invoice', useI18nKeyAsIconKey: true },
  standard_pay_in_advance: { i18nKey: 'payInAdvance' },
  standard_cod: { i18nKey: 'cod' },
  bg_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  cy_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  cz_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  dhl_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  ee_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  es_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  gr_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  hr_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  hu_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  it_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  lt_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  lv_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  pl_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  pt_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  ro_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  sa_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  si_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  sk_cod: { i18nKey: 'cod', useI18nKeyAsIconKey: true },
  accounting: { i18nKey: 'accountingB2b' },
  adyen_apple_pay: { i18nKey: 'applePay', useI18nKeyAsIconKey: true },
  adyen_creditcard: { i18nKey: 'creditCard', iconKeys: ['mastercard', 'visa'], form: AdyenCreditcardPayment },
  adyen_google_pay: { i18nKey: 'googlePay', useI18nKeyAsIconKey: true },
  adyen_ideal: { i18nKey: 'iDeal', useI18nKeyAsIconKey: true },
  adyen_klarna_paynow: { i18nKey: 'klarnaPayNow', iconKeys: ['klarna'] },
  adyen_klarna_paylater: { i18nKey: 'klarnaPayLater', iconKeys: ['klarna'] },
  adyen_klarna_installments: { i18nKey: 'klarnaInstallments', iconKeys: ['klarna'] },
  adyen_mbway: { i18nKey: 'mbway', useI18nKeyAsIconKey: true, modal: 'always' },
  adyen_mobilepay: { i18nKey: 'mobilePay', useI18nKeyAsIconKey: true },
  adyen_paypal: { i18nKey: 'payPal', useI18nKeyAsIconKey: true },
  adyen_online_banking_pl: { i18nKey: 'p24', useI18nKeyAsIconKey: true, form: AdyenOnlineBankingPlPayment },
  adyen_twint: { i18nKey: 'twint', useI18nKeyAsIconKey: true },
  afterpay_invoice: { i18nKey: 'afterpayInvoice', form: AfterpayPayment, iconKeys: ['afterPay'] },
  amazon_pay: { i18nKey: 'amazonPay', useI18nKeyAsIconKey: true, form: AmazonPayment },
  arvato_creditcard: { i18nKey: 'creditCard', useI18nKeyAsIconKey: true, modal: 'always' },
  arvato_p24: { i18nKey: 'p24', useI18nKeyAsIconKey: true },
  arvato_paypal: { i18nKey: 'payPal', useI18nKeyAsIconKey: true },
  arvato_ideal: { i18nKey: 'iDeal', useI18nKeyAsIconKey: true, modal: 'always' },
  computop_applepay: { i18nKey: 'applePay', useI18nKeyAsIconKey: true },
  computop_bancontact: { i18nKey: 'bancontact', useI18nKeyAsIconKey: true, modal: 'desktopOnly' },
  computop_creditcard: { i18nKey: 'creditCard', iconKeys: ['mastercard', 'visa'], modal: 'desktopOnly' },
  computop_creditcard_mastercard: { i18nKey: 'mastercard', useI18nKeyAsIconKey: true, modal: 'desktopOnly' },
  computop_creditcard_visa: { i18nKey: 'visa', useI18nKeyAsIconKey: true, modal: 'desktopOnly' },
  computop_googlepay: { i18nKey: 'googlePay', useI18nKeyAsIconKey: true },
  computop_maestro: { i18nKey: 'debitCard', iconKeys: ['maestro'], modal: 'desktopOnly' },
  computop_paydirekt: { i18nKey: 'giropay', useI18nKeyAsIconKey: true },
  computop_payu: { i18nKey: 'payU', useI18nKeyAsIconKey: true },
  computop_payu_cee: { i18nKey: 'payU', useI18nKeyAsIconKey: true },
  computop_payu_twisto: { i18nKey: 'twisto', useI18nKeyAsIconKey: true },
  computop_postfinance: { i18nKey: 'postFinance', useI18nKeyAsIconKey: true },
  klarna_paylater: { i18nKey: 'klarnaPayLater', form: KlarnaPayment, iconKeys: ['klarna'], modal: 'always' },
  klarna_paynow_direct_debit: {
    i18nKey: 'sepaDirectDebit',
    form: KlarnaPayment,
    iconKeys: ['sepaDirectDebit', 'klarna'], modal: 'always'
  },
  klarna_paynow_direct_bank_transfer: { i18nKey: 'klarnaPayNow', form: KlarnaPayment, iconKeys: ['klarna'], modal: 'always' },
  klarna_installments: { i18nKey: 'klarnaInstallments', form: KlarnaPayment, iconKeys: ['klarna'], modal: 'always' },
  klarna_paynow: { i18nKey: 'klarnaPayNow', form: KlarnaPayment, iconKeys: ['klarna'], modal: 'always' },
  paypal: { i18nKey: 'payPal', useI18nKeyAsIconKey: true },
  paypal_instant: { i18nKey: 'payPal', useI18nKeyAsIconKey: true, form: PaypalPayment },
  ratepay_directdebit: { i18nKey: 'sepaDirectDebit', form: RatepayPayment, useI18nKeyAsIconKey: true },
  ratepay_invoice: {
    i18nKey: 'invoice',
    form: RatepayPayment,
    iconKeys: ['ratepay'],
    localeIconKeys: {
      en_DE: ['ratepayInvoiceEn'],
      de_DE: ['ratepayInvoiceDe'],
      de_CH: ['ratepayInvoiceDe'],
      fr_BE: ['ratepayInvoiceFr'],
      fr_CH: ['ratepayInvoiceFr'],
      nl_NL: ['ratepayInvoiceNl'],
      nl_BE: ['ratepayInvoiceNl']
    }
  },
  saferpay_bancontact: { i18nKey: 'bancontact', useI18nKeyAsIconKey: true },
  saferpay_creditcard_maestro: { i18nKey: 'debitCard', iconKeys: ['maestro'] },
  saferpay_creditcard_mastercard: { i18nKey: 'mastercard', useI18nKeyAsIconKey: true },
  saferpay_creditcard_visa: { i18nKey: 'visa', useI18nKeyAsIconKey: true },
  saferpay_ideal: { i18nKey: 'iDeal', useI18nKeyAsIconKey: true },
  saferpay_p24: { i18nKey: 'p24', useI18nKeyAsIconKey: true },
  saferpay_twint: { i18nKey: 'twint', useI18nKeyAsIconKey: true },
  spay_adyen_apple_pay: { i18nKey: 'applePay', useI18nKeyAsIconKey: true },
  spay_adyen_blik: { i18nKey: 'blik', useI18nKeyAsIconKey: true, form: AdyenBlikPayment },
  spay_adyen_credit_card: { i18nKey: 'creditCard', useI18nKeyAsIconKey: true, form: AdyenCreditcardPayment },
  spay_adyen_creditcard_bc: { i18nKey: 'bancontact', useI18nKeyAsIconKey: true, form: AdyenBancontactCardPayment },
  spay_adyen_google_pay: { i18nKey: 'googlePay', useI18nKeyAsIconKey: true },
  spay_adyen_ideal: { i18nKey: 'iDeal', useI18nKeyAsIconKey: true },
  spay_adyen_mobilepay: { i18nKey: 'mobilePay', useI18nKeyAsIconKey: true },
  spay_adyen_online_banking_pl: { i18nKey: 'p24', useI18nKeyAsIconKey: true, form: AdyenOnlineBankingPlPayment },
  spay_adyen_twint: { i18nKey: 'twint', useI18nKeyAsIconKey: true },
  spay_klarna_paynow: { i18nKey: 'klarnaPayNow', form: KlarnaPayment, iconKeys: ['klarna'], modal: 'always' },
  spay_paypal: { i18nKey: 'payPal', useI18nKeyAsIconKey: true, form: PaypalPayment },
  spay_klarna_paylater: { i18nKey: 'klarnaPayLater', form: KlarnaPayment, iconKeys: ['klarna'], modal: 'always' },
  spay_klarna_payovertime: { i18nKey: 'klarnaInstallments', form: KlarnaPayment, iconKeys: ['klarna'], modal: 'always' },
  unzer_bancontact: { i18nKey: 'bancontact', useI18nKeyAsIconKey: true },
  unzer_creditcard: { i18nKey: 'creditCard', useI18nKeyAsIconKey: true, form: UnzerCreditcard },
  unzer_ideal: { i18nKey: 'iDeal', useI18nKeyAsIconKey: true, form: UnzerIdeal },
  unzer_installment: { i18nKey: 'installments', form: UnzerInstallment },
  unzer_klarna: { i18nKey: 'unzerKlarna', iconKeys: ['klarna'] },
  unzer_postfinance_card: { i18nKey: 'postFinance', useI18nKeyAsIconKey: true },
  unzer_p24: { i18nKey: 'p24', useI18nKeyAsIconKey: true },
  witt_cod: { i18nKey: 'cod' },
  witt_deferred_invoice: { i18nKey: 'deferredInvoice' },
  witt_invoice: { i18nKey: 'invoice' },
  witt_pay_instalments: { i18nKey: 'installments', form: InstallmentsForm }
};

export default additionalPaymentConfig;
